import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownFilter } from '../../components/Dropdown/DropdownVariants/DropdownFilter';
import { usePrincipals } from '../../hooks/queries';
import { getCurrentPrincipal } from '../../lib/currentPrincipal';
import { getMyAssignments } from '../../utils/principal-service/principal-endpoints';

import { Button, ButtonType } from '../../components/Button';
import { DropdownOptionProps } from '../../components/Dropdown/helper/DropdownTypes';
import { LoadingBounce } from '../../components/Loading';
import {
  GlobalFilter,
  useGlobalFilters,
} from '../../context/GlobalFilterProvider';
import {
  AssignedCountry,
  AssignedMarketSegment,
  AssignedPortfolio,
  AssignedTerritory,
} from '../../utils/principal-service/dto/Assignments.dto';

const portfolioToDropdownOption = (
  p: AssignedPortfolio,
): DropdownOptionProps => ({
  name: p.name,
  value: p.id,
});

const marketSegmentToDropdownOption = (
  p: AssignedMarketSegment,
): DropdownOptionProps => ({
  name: p.name,
  value: p.id,
});

const territoryToDropdownOption = (
  t: AssignedTerritory,
): DropdownOptionProps => ({
  name: t.name,
  value: t.name,
});

export enum GlobalFilterType {
  PORTFOLIOS,
  MARKET_SEGMENTS,
  COUNTRIES,
  TERRITORIES,
}

type GlobalFiltersProps = {
  disabledFilters: readonly GlobalFilterType[];
  onSave?: (newFilter: GlobalFilter) => void;
};

export const GlobalFilters: React.FC<GlobalFiltersProps> = ({
  disabledFilters,
  onSave,
}) => {
  const { t } = useTranslation();
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const { globalFilters, setGlobalFilters } = useGlobalFilters();
  const [unpublishedFilters, setUnpublishedFilters] =
    useState<GlobalFilter>(globalFilters);
  const [disableApplyButton, setDisableApplyButton] = useState(true);
  const [visualLoading, setVisualLoading] = useState(true);
  const { data: assignments, isLoading } = useQuery({
    queryFn: () => getMyAssignments(currentPrincipal.id),
    queryKey: ['myAssignments'],
  });

  useEffect(() => {
    if (isLoading || !assignments) {
      setVisualLoading(true);
      return;
    }
    setVisualLoading(false);
  }, [assignments, isLoading]);

  useEffect(() => {
    if (!assignments) return;

    if (
      globalFilters.countries === null ||
      globalFilters.portfolios === null ||
      globalFilters.marketSegments === null
    ) {
      const newInitialFilter: GlobalFilter = {
        allCountriesSelected: true,
        countries: assignments.countries,
        allPortfoliosSelected: true,
        portfolios: assignments.portfolios,
        allMarketSegmentsSelected: true,
        marketSegments: assignments.marketSegments,
        allTerritoriesSelected: true,
        territories: assignments.territories ?? null,
      };

      setGlobalFilters(newInitialFilter);

      setUnpublishedFilters(newInitialFilter);
    }
  }, [
    assignments,
    globalFilters.countries,
    globalFilters.marketSegments,
    globalFilters.portfolios,
    setGlobalFilters,
  ]);

  const dropdownOptionToPortfolio = (
    d: DropdownOptionProps,
  ): AssignedPortfolio | undefined =>
    assignments?.portfolios.find((p) => p.id === d.value);
  const dropdownOptionToMarketSegment = (
    d: DropdownOptionProps,
  ): AssignedMarketSegment | undefined =>
    assignments?.marketSegments.find((p) => p.id === d.value);
  const dropdownOptionToCountry = (
    d: DropdownOptionProps,
  ): AssignedCountry | undefined =>
    assignments?.countries.find((c) => c.iso === d.value);
  const dropdownOptionToTerritory = (
    d: DropdownOptionProps,
  ): AssignedTerritory | undefined =>
    (assignments?.territories ?? []).find((c) => c.name === d.value);

  const regions = Array.from(
    new Set(
      assignments?.countries
        .filter((c) => c.region !== null)
        .map((c) => c.region),
    ),
  )
    .filter((r): r is string => r !== null)
    .sort((a, b) => a.localeCompare(b));

  return (
    <>
      <div className="relative">
        {visualLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded bg-white/60 backdrop-blur-[1px] transition-all duration-300">
            <LoadingBounce />
          </div>
        )}
        <div
          className={`flex w-full items-end transition-opacity duration-300 ${
            visualLoading ? 'pointer-events-none opacity-70' : 'opacity-100'
          }`}
        >
          <DropdownFilter
            typeAhead
            selectAllLabel={t('placeholders:selectAll')}
            deselectAllLabel={t('placeholders:deselectAll')}
            label={t('features:reporting:filters:portfolio')}
            options={(assignments?.portfolios ?? []).map(
              portfolioToDropdownOption,
            )}
            initialSelection={(
              unpublishedFilters.portfolios ??
              assignments?.portfolios ??
              []
            ).map(portfolioToDropdownOption)}
            disabledMessage={t('placeholders:dropdownAll')}
            onChange={(options) => {
              setUnpublishedFilters({
                ...unpublishedFilters,
                allPortfoliosSelected:
                  assignments?.portfolios.length === options.length,
                portfolios: options
                  .map(dropdownOptionToPortfolio)
                  .filter((p): p is AssignedPortfolio => p !== undefined),
              });
              setDisableApplyButton(false);
            }}
            disabled={
              disabledFilters.includes(GlobalFilterType.PORTFOLIOS) ||
              visualLoading
            }
          />
          <div className="mx-3">
            <DropdownFilter
              typeAhead
              label={t('features:reporting:filters:country')}
              selectAllLabel={t('placeholders:selectAll')}
              deselectAllLabel={t('placeholders:deselectAll')}
              options={regions.map((region) => {
                return {
                  name: region,
                  value: region,
                  childOptions: (assignments?.countries ?? [])
                    .filter((c) => c.region === region)
                    .map((c) => ({ name: c.name, value: c.iso }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
                };
              })}
              initialSelection={(
                unpublishedFilters.countries ??
                assignments?.countries ??
                []
              )
                .filter((c) => c.region !== null)
                .map((c) => {
                  return {
                    name: c.name,
                    value: c.iso,
                    active: true,
                  };
                })}
              onChange={(options) => {
                setUnpublishedFilters({
                  ...unpublishedFilters,
                  allCountriesSelected:
                    assignments?.countries.length === options.length,
                  countries: options
                    .map(dropdownOptionToCountry)
                    .filter((c): c is AssignedCountry => c !== undefined),
                });
                setDisableApplyButton(false);
              }}
              disabled={
                disabledFilters.includes(GlobalFilterType.COUNTRIES) ||
                visualLoading
              }
            />
          </div>
          {assignments?.territories?.length &&
          assignments?.territories.length > 0 ? (
            <div className="mr-3">
              <DropdownFilter
                typeAhead
                label={t('features:reporting:filters:territory')}
                selectAllLabel={t('placeholders:selectAll')}
                deselectAllLabel={t('placeholders:deselectAll')}
                options={assignments.territories.map(territoryToDropdownOption)}
                initialSelection={(
                  unpublishedFilters.territories ?? assignments.territories
                ).map((t) => {
                  return {
                    name: t.name,
                    value: t.name,
                    active: true,
                  };
                })}
                onChange={(options) => {
                  setUnpublishedFilters({
                    ...unpublishedFilters,
                    allTerritoriesSelected:
                      assignments.territories?.length === options.length,
                    territories: options
                      .map(dropdownOptionToTerritory)
                      .filter((t): t is AssignedTerritory => t !== undefined),
                  });
                  setDisableApplyButton(false);
                }}
                disabled={
                  disabledFilters.includes(GlobalFilterType.TERRITORIES) ||
                  (!!unpublishedFilters.countries &&
                    !unpublishedFilters.countries.some(
                      ({ iso }) => iso === 'USA',
                    )) ||
                  visualLoading
                }
              />
            </div>
          ) : null}

          <DropdownFilter
            typeAhead
            selectAllLabel={t('placeholders:selectAll')}
            deselectAllLabel={t('placeholders:deselectAll')}
            label={t('features:reporting:filters:marketSegment')}
            options={(assignments?.marketSegments ?? []).map(
              marketSegmentToDropdownOption,
            )}
            initialSelection={(
              unpublishedFilters.marketSegments ??
              assignments?.marketSegments ??
              []
            ).map(marketSegmentToDropdownOption)}
            disabledMessage={t('placeholders:dropdownAll')}
            onChange={(options) => {
              setUnpublishedFilters({
                ...unpublishedFilters,
                allMarketSegmentsSelected:
                  assignments?.marketSegments.length === options.length,
                marketSegments: options
                  .map(dropdownOptionToMarketSegment)
                  .filter((m): m is AssignedMarketSegment => m !== undefined),
              });
              setDisableApplyButton(false);
            }}
            disabled={
              disabledFilters.includes(GlobalFilterType.MARKET_SEGMENTS) ||
              visualLoading
            }
          />
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <Button
          className="min-h-40px flex items-center"
          disabled={disableApplyButton || isLoading}
          onClick={() => {
            setGlobalFilters(unpublishedFilters);

            setDisableApplyButton(true);
            if (onSave) {
              onSave(unpublishedFilters);
            }
          }}
          buttonType={ButtonType.ICON}
          label="Apply filter settings"
        />
      </div>
    </>
  );
};
